import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles,
  LogOut,
  ExternalLink,
  CreditCard,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import SiteHeader from "@/components/SiteHeader";
import Footer from "@/components/Footer";
import { useMemberstack, getFirstName, getPlanLabels, isAdmin } from "@/lib/memberstack";

/**
 * Build a short-lived 1% Club access token URL for paying members.
 *
 * Why: Memberstack's custom-domain cookie sharing isn't active yet (pending
 * Memberstack support activation). Until it is, paid members visiting our
 * other Weave subdomains (loom, linkedin-read, …) are treated as guests.
 * To unblock them now, we pass a small claim-based token in the URL —
 * each destination app verifies it and grants member access.
 *
 * Token shape (URL-safe base64 of JSON):
 *   { v: 1, e: <memberEmail>, p: <planId>, iat: <unix-seconds>, exp: <unix-seconds> }
 *
 * Lifetime: 24 hours. Not cryptographically signed — friendly-fence, not a
 * real auth boundary. Retired the moment Memberstack's custom-domain cookie
 * SSO starts working.
 */
function buildMemberTokenUrlFactory(baseUrl) {
  return function buildUrl({ member, plans }) {
    // Memberstack DOM v2 SDK can return email at member.auth.email OR member.email
    const email = member?.auth?.email || member?.email;
    if (!email) {
      // eslint-disable-next-line no-console
      console.warn("[wm-token] no member email — falling back to bare URL", { baseUrl, member });
      return baseUrl;
    }

    const activePlan = (member.planConnections || []).find(
      (p) => p.active !== false && p.status !== "CANCELED"
    );
    if (!activePlan) {
      // eslint-disable-next-line no-console
      console.warn("[wm-token] no active plan — falling back to bare URL", {
        baseUrl,
        planConnections: member.planConnections,
      });
      return baseUrl;
    }

    // Prefer Stripe priceId (prc_...) since downstream apps (loom, linkedin-read)
    // match against the priceId list. Fall back to other identifiers if absent.
    const planIdForToken =
      activePlan.payment?.priceId ||
      activePlan.priceId ||
      activePlan.planId ||
      activePlan.id ||
      (plans && plans[0]) ||
      "";

    const now = Math.floor(Date.now() / 1000);
    const claims = {
      v: 1,
      e: email,
      p: planIdForToken,
      iat: now,
      exp: now + 24 * 60 * 60,
    };
    const json = JSON.stringify(claims);
    const b64 = btoa(unescape(encodeURIComponent(json)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
    const sep = baseUrl.includes("?") ? "&" : "?";
    const finalUrl = `${baseUrl}${sep}wm=${b64}`;
    // eslint-disable-next-line no-console
    console.log("[wm-token] issued", { baseUrl, planIdForToken, claims });
    return finalUrl;
  };
}

const buildLoomMemberUrl = buildMemberTokenUrlFactory(
  "https://loom.wearetheweave.co.uk"
);
const buildLinkedInAuditUrl = buildMemberTokenUrlFactory(
  "https://linkedin-read.wearetheweave.co.uk"
);
const buildCatchMemberUrl = buildMemberTokenUrlFactory(
  "https://catch.wearetheweave.co.uk"
);
const buildPisaMemberUrl = buildMemberTokenUrlFactory(
  "https://pisa.wearetheweave.co.uk"
);
const buildOpportunityUnlockUrl = buildMemberTokenUrlFactory(
  "https://opportunity-unlock.wearetheweave.co.uk"
);

/**
 * FEED-shaped founding member dashboard.
 * Each quadrant holds one or more concrete "offers" — real links members can act on today.
 * Action types:
 *   - internal:  uses react-router <Link>
 *   - external:  opens in new tab (https://… or mailto:…)
 */
const FEED_QUADRANTS = [
  {
    key: "facilitation",
    letter: "F",
    label: "Facilitation",
    color: "#4aa0a4",
    blurb:
      "Real introductions. Real conversations. Start with the two below — your gateway to the wider Inner Circle.",
    active: true,
    actions: [
      {
        label: "Book your 15-min welcome call",
        href: "https://tidycal.com/the-weave/welcome-to-the-1-club",
        kind: "external",
        primary: true,
      },
      {
        label: "Connect with James on LinkedIn",
        href: "https://linkedin.com/in/cracknelljames121",
        kind: "external",
      },
    ],
    note: "After your welcome call we'll invite you to The Weave Networker — our Chrome extension + Inner Circle.",
  },
  {
    key: "education",
    letter: "E",
    label: "Education",
    color: "#f4b847",
    blurb:
      "Practical tools and clinics — built so you can use them in 15 minutes, not 15 weeks.",
    active: true,
    actions: [
      {
        label: "Open the Stay AWAKE tool",
        href: "/",
        kind: "internal",
        primary: true,
      },
      {
        label: "Pause with PISA — for the in-the-moment decisions",
        href: "https://pisa.wearetheweave.co.uk",
        kind: "external",
        buildUrl: buildPisaMemberUrl,
      },
      {
        label: "Run your Quarterly Scan (Catch the Wave)",
        href: "https://catch.wearetheweave.co.uk",
        kind: "external",
        buildUrl: buildCatchMemberUrl,
      },
      {
        label: "Map your funding options at Funding Hub",
        href: "https://funding-hub.wearetheweave.co.uk",
        kind: "external",
      },
    ],
  },
  {
    key: "entertainment",
    letter: "E",
    label: "Entertainment",
    color: "#3c8386",
    blurb:
      "Lighter rituals. The play that makes the work bearable.",
    active: true,
    actions: [
      {
        label: "Pull today's thread",
        href: "https://loom.wearetheweave.co.uk",
        kind: "external",
        primary: true,
        buildUrl: buildLoomMemberUrl,
      },
      {
        label: "Play Pointless Startup",
        href: "https://pointless-startup.wearetheweave.co.uk",
        kind: "external",
      },
    ],
    note: "Coming soon: a 1% Club edition of Pointless Startup with a monthly league table and prizes.",
  },
  {
    key: "development",
    letter: "D",
    label: "Development",
    color: "#e93e36",
    blurb:
      "Grow the business. Get on the mic. Sharpen your funding instinct.",
    active: true,
    actions: [
      {
        label: "Run your LinkedIn Audit",
        href: "https://linkedin-read.wearetheweave.co.uk",
        kind: "external",
        primary: true,
        buildUrl: buildLinkedInAuditUrl,
      },
      {
        label: "Diagnose a growth plateau with Opportunity Unlock",
        href: "https://opportunity-unlock.wearetheweave.co.uk",
        kind: "external",
        buildUrl: buildOpportunityUnlockUrl,
      },
      {
        label: "Book a 20-min funding clinic",
        href: "https://tidycal.com/the-weave/funding-clinic",
        kind: "external",
      },
      {
        label: "Pitch + book your Interwoven Podcast interview",
        href: "https://tidycal.com/the-weave/interwoven-podcast-interview",
        kind: "external",
      },
      {
        label: "Talk wellbeing with Sandrine (FounderThrive)",
        href: "mailto:sandrine@wearetheweave.co.uk?subject=Wellbeing%20and%20FounderThrive",
        kind: "external",
      },
    ],
    note: "The LinkedIn Audit is the current entry point — it will evolve into the full Branding Coach.",
  },
];

const WEAVE_HOME = "https://www.wearetheweave.co.uk";

export default function Dashboard() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { memberstack, currentMember, refreshMember } = useMemberstack();
  const [signingOut, setSigningOut] = useState(false);

  const firstName = getFirstName(currentMember) || "founder";
  const planLabels = getPlanLabels(currentMember);
  const planSummary = planLabels.length ? planLabels.join(" · ") : null;
  const admin = isAdmin(currentMember);

  // Refresh member data once on mount in case we just came back from Stripe checkout
  useEffect(() => {
    refreshMember?.();
    if (params.get("welcome") === "1") {
      toast.success("Welcome to the 1% Club", {
        description: "You're in. We'll be in touch as each room opens.",
      });
      params.delete("welcome");
      setParams(params, { replace: true });
    }
    if (params.get("cancelled") === "1") {
      toast("Checkout cancelled — no payment taken.", {
        description: "You're still signed in. Your membership is unchanged.",
      });
      params.delete("cancelled");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignOut = async () => {
    if (!memberstack) return;
    setSigningOut(true);
    try {
      await memberstack.logout();
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error("Couldn't sign out", { description: err?.message });
      setSigningOut(false);
    }
  };

  const handleBilling = async () => {
    if (!memberstack) return;
    try {
      await memberstack.launchStripeCustomerPortal({
        returnUrl: WEAVE_HOME,
      });
    } catch (err) {
      toast.error("Couldn't open billing", { description: err?.message });
    }
  };

  return (
    <div className="App" data-testid="dashboard-page">
      <SiteHeader onLogoClick={() => window.location.assign(WEAVE_HOME)} />

      <main className="max-w-3xl mx-auto px-6 pt-24 pb-20">
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <p
              className="text-[11px] uppercase tracking-[0.22em] font-medium"
              style={{ color: "#3c8386" }}
              data-testid="dashboard-eyebrow"
            >
              The Weave's 1% Club · {admin ? "Team" : "Founding member"}
              {admin && (
                <span
                  className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-[0.16em]"
                  style={{
                    background: "#e93e3614",
                    color: "#b8302a",
                    letterSpacing: "0.14em",
                  }}
                  data-testid="dashboard-admin-badge"
                >
                  ADMIN
                </span>
              )}
            </p>
            <div className="flex items-center gap-2">
              {planLabels.length > 0 && (
                <button
                  type="button"
                  onClick={handleBilling}
                  className="weave-btn-outline text-sm"
                  data-testid="dashboard-billing-btn"
                >
                  <CreditCard size={14} />
                  Manage billing
                </button>
              )}
              <button
                type="button"
                onClick={handleSignOut}
                className="weave-btn-ghost text-sm"
                disabled={signingOut}
                data-testid="dashboard-signout-btn"
              >
                <LogOut size={14} />
                {signingOut ? "Signing out…" : "Sign out"}
              </button>
            </div>
          </div>

          <h1
            className="text-4xl sm:text-5xl font-light text-slate-800 tracking-tight leading-[1.05]"
            data-testid="dashboard-welcome"
          >
            Welcome, {firstName}.
          </h1>
          <p className="mt-4 text-lg font-light text-slate-600 leading-relaxed max-w-2xl">
            {admin
              ? "You're inside the room you're building. As each space opens, you'll see member tools here ahead of everyone else."
              : "You're a founding member of The Weave's 1% Club. Four rooms are opening over the coming weeks — your home will fill out as each one arrives."}
          </p>
          {planSummary && (
            <p className="mt-3 text-sm text-slate-400" data-testid="dashboard-plan-summary">
              Your plan: {planSummary}
            </p>
          )}
        </motion.section>

        <div className="weave-divider" />

        <section className="weave-feed-grid" data-testid="feed-grid">
          {FEED_QUADRANTS.map((q, idx) => (
            <motion.div
              key={q.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                delay: 0.05 * idx,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="weave-feed-card"
              data-testid={`feed-${q.key}`}
              style={{
                borderColor: q.active
                  ? `${q.color}55`
                  : "#e2e8f0",
                background: q.active ? `${q.color}07` : "#ffffff",
              }}
            >
              <div className="flex items-center gap-3 mb-3">
                <span
                  className="weave-feed-letter"
                  style={{ background: q.color }}
                >
                  {q.letter}
                </span>
                <h3 className="text-xl font-medium tracking-tight text-slate-800">
                  {q.label}
                </h3>
                {q.active && (
                  <span
                    className="text-[10px] uppercase tracking-[0.16em] font-medium px-2 py-0.5 rounded-full ml-auto"
                    style={{
                      background: `${q.color}1f`,
                      color: q.color,
                    }}
                  >
                    Open
                  </span>
                )}
              </div>
              <p className="text-slate-600 leading-relaxed mb-4">
                {q.blurb}
              </p>

              {q.actions?.length > 0 && (
                <div className="flex flex-col gap-2 mb-3" data-testid={`feed-${q.key}-actions`}>
                  {q.actions.map((a, i) => {
                    const testid = `feed-${q.key}-action-${i}`;
                    const className = a.primary
                      ? "weave-feed-action is-primary"
                      : "weave-feed-action";
                    const inner = (
                      <>
                        <span>{a.label}</span>
                        {a.kind === "external" ? (
                          <ExternalLink size={14} />
                        ) : (
                          <ArrowRight size={14} />
                        )}
                      </>
                    );

                    // If the action provides a buildUrl function (e.g. to attach
                    // a member token), compute the href NOW at render time so the
                    // <a> tag has the correct URL in the DOM. Rewriting the href
                    // inside an onClick handler is racy — the browser starts
                    // navigation before the handler can mutate the attribute.
                    const computedHref =
                      typeof a.buildUrl === "function"
                        ? a.buildUrl({ member: currentMember, plans: planLabels })
                        : a.href;

                    return a.kind === "internal" ? (
                      <Link
                        key={i}
                        to={computedHref}
                        className={className}
                        style={a.primary ? { borderColor: q.color, color: q.color } : undefined}
                        data-testid={testid}
                      >
                        {inner}
                      </Link>
                    ) : (
                      <a
                        key={i}
                        href={computedHref}
                        target={computedHref.startsWith("mailto:") ? "_self" : "_blank"}
                        rel="noopener noreferrer"
                        className={className}
                        style={a.primary ? { borderColor: q.color, color: q.color } : undefined}
                        data-testid={testid}
                      >
                        {inner}
                      </a>
                    );
                  })}
                </div>
              )}

              {q.note && (
                <p className="text-xs text-slate-400 leading-relaxed mt-2" data-testid={`feed-${q.key}-note`}>
                  {q.note}
                </p>
              )}

              {!q.active && q.status && (
                <p className="text-sm text-slate-400" data-testid={`feed-${q.key}-status`}>
                  {q.status}
                </p>
              )}
            </motion.div>
          ))}
        </section>

        <section className="mt-12 weave-card-soft" data-testid="founding-note">
          <div className="flex items-start gap-3">
            <Sparkles size={18} style={{ color: "#3c8386", marginTop: 2 }} />
            <div>
              <p className="text-sm font-medium text-slate-700 mb-1">
                You're in early.
              </p>
              <p className="text-sm text-slate-600 leading-relaxed">
                Founding members shape the rooms as they open. Reply to any of
                our emails with what you'd most like to see first — it goes
                straight to a human, not a bot.
              </p>
            </div>
          </div>
        </section>

        <a
          href="https://www.wearetheweave.co.uk"
          target="_blank"
          rel="noopener noreferrer"
          className="weave-learn-more group block mt-8"
          data-testid="dashboard-back-to-weave"
        >
          <div>
            <p
              className="text-[11px] uppercase tracking-[0.22em] font-medium"
              style={{ color: "#3c8386" }}
            >
              The main thing
            </p>
            <h3 className="mt-2 text-xl md:text-2xl font-light text-slate-800 tracking-tight leading-snug">
              Most of what we do happens at wearetheweave.co.uk
            </h3>
            <p className="mt-2 text-slate-500 text-sm md:text-base">
              Community, programmes, the podcast, blog, LinkedIn lives.
              The rooms here will deepen — pop back to the main site any time.
            </p>
          </div>
          <span
            className="weave-btn-primary flex-shrink-0"
            style={{ pointerEvents: "none" }}
          >
            Visit The Weave
            <ExternalLink size={16} />
          </span>
        </a>

        <section className="mt-8 text-sm text-slate-400" data-testid="dashboard-meta">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span>
              Signed in as{" "}
              <span className="text-slate-600">
                {currentMember?.auth?.email || currentMember?.email}
              </span>
            </span>
            <a
              href="https://www.wearetheweave.co.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-slate-500 hover:text-[#3c8386] transition-colors"
              data-testid="dashboard-weave-link"
            >
              wearetheweave.co.uk
              <ExternalLink size={12} />
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
